/**
 * Admin Memory Modal - עריכת זיכרון AI של לקוח (מנהל)
 * Lets an admin/advisor view, edit, or clear any user's AI memory.
 */

import { useEffect, useState } from 'react'
import { Brain, X, Trash2, Loader2 } from 'lucide-react'
import api, { handleApiError } from '../../services/api'
import MemoryEditor, { MemoryPayload, MemoryPatch } from '../chat/MemoryEditor'

interface AdminMemoryModalProps {
  userId: string
  userName: string
  onClose: () => void
}

export default function AdminMemoryModal({ userId, userName, onClose }: AdminMemoryModalProps) {
  const [memory, setMemory] = useState<MemoryPayload | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [isClearing, setIsClearing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadMemory()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId])

  const loadMemory = async () => {
    setIsLoading(true)
    setError(null)
    try {
      const res = await api.get(`/admin/users/${userId}/memory`)
      setMemory(res.data?.memory ?? null)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsLoading(false)
    }
  }

  const handleSave = async (patch: MemoryPatch) => {
    setIsSaving(true)
    setError(null)
    try {
      const res = await api.put(`/admin/users/${userId}/memory`, patch)
      setMemory(res.data?.memory ?? null)
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsSaving(false)
    }
  }

  const handleClear = async () => {
    if (!window.confirm(`למחוק את כל זיכרון ה-AI של ${userName}? פעולה זו אינה הפיכה.`)) return
    setIsClearing(true)
    setError(null)
    try {
      await api.delete(`/admin/users/${userId}/memory`)
      await loadMemory()
    } catch (err) {
      setError(handleApiError(err))
    } finally {
      setIsClearing(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      dir="rtl"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl shadow-xl w-full max-w-lg max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 sticky top-0 bg-white">
          <div className="flex items-center gap-2">
            <Brain size={20} className="text-primary-600" aria-hidden="true" />
            <h2 className="text-base font-heading font-bold text-gray-900">
              זיכרון AI — {userName}
            </h2>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="סגור">
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4">
          {isLoading ? (
            <div className="flex items-center justify-center py-8 text-gray-500">
              <Loader2 size={22} className="animate-spin" aria-hidden="true" />
            </div>
          ) : (
            <>
              <MemoryEditor
                initial={memory ?? EMPTY_MEMORY}
                saving={isSaving}
                error={error}
                onSave={handleSave}
                onCancel={onClose}
              />
              <div className="mt-4 pt-4 border-t border-gray-200 flex justify-start">
                <button
                  onClick={handleClear}
                  disabled={isClearing}
                  className="btn btn-outline text-sm text-red-600 border-red-200 hover:bg-red-50 flex items-center gap-1.5"
                >
                  {isClearing ? (
                    <Loader2 size={15} className="animate-spin" aria-hidden="true" />
                  ) : (
                    <Trash2 size={15} aria-hidden="true" />
                  )}
                  מחק זיכרון
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

const EMPTY_MEMORY: MemoryPayload = {
  enabled: true,
  autoUpdateEnabled: true,
  hasMemory: false,
  summary: '',
  topics: [],
  structured: { keyFacts: [], preferences: [], concerns: [] },
  lastSummarizedAt: null,
}
